import { ReactNode, useEffect, useRef } from 'react'
import cx from 'classnames'
import styles from './HeroInTune.module.css'
import { startWaveline } from './waveline'

interface HeroInTuneProps {
  /** Small uppercase label shown in a pill above the title */
  eyebrow?: ReactNode
  /** The main hero heading */
  title: ReactNode
  /** Supporting copy shown under the title */
  subtitle?: ReactNode
  /** Call-to-action buttons or links shown under the subtitle */
  actions?: ReactNode
  /** Minimum height of the hero */
  minHeight?: string
  /**
   * Force the hero to render in a single colour scheme instead of
   * following the system preference. Sets `color-scheme` on the
   * wrapper so the design tokens resolve accordingly, and the
   * waveline animation draws with the matching palette.
   */
  colorScheme?: 'light' | 'dark'
  /** Additional CSS class */
  className?: string
}

/**
 * Full-bleed marketing hero with the animated "in tune" waveline
 * background: three sine voices (tonic, perfect fourth and fifth)
 * braided in the just-intonation ratio 6 : 8 : 9, with particles
 * that brighten where two voices meet in unison.
 *
 * The animation respects prefers-reduced-motion (a single still
 * frame is drawn instead) and pauses while the tab is hidden.
 *
 * On prerendered pages the SPA never boots, so the effect below
 * never runs. There, `waveline.entry.ts` starts the animation
 * instead — it finds the hero by the `data-hero-in-tune*`
 * attributes, which is why those are on the markup rather than
 * relying on the CSS module class names (those are hashed and
 * change every build).
 */
export function HeroInTune({
  eyebrow,
  title,
  subtitle,
  actions,
  minHeight = '100vh',
  colorScheme,
  className,
}: HeroInTuneProps) {
  const hostRef = useRef<HTMLElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!hostRef.current || !canvasRef.current) return
    return startWaveline(canvasRef.current, hostRef.current, {
      colorScheme,
    })
  }, [colorScheme])

  return (
    <section
      ref={hostRef}
      className={cx(styles.HeroInTune, className, {
        [styles.isSchemeLight]: colorScheme === 'light',
        [styles.isSchemeDark]: colorScheme === 'dark',
      })}
      style={{ minHeight }}
      data-hero-in-tune=""
      data-hero-in-tune-scheme={colorScheme}
    >
      <canvas
        ref={canvasRef}
        className={styles.HeroInTune__canvas}
        aria-hidden="true"
        data-hero-in-tune-canvas=""
      />
      <div className={styles.HeroInTune__content}>
        {eyebrow && (
          <span className={styles.HeroInTune__eyebrow}>
            {eyebrow}
          </span>
        )}
        <h1 className={styles.HeroInTune__title}>{title}</h1>
        {subtitle && (
          <p className={styles.HeroInTune__subtitle}>
            {subtitle}
          </p>
        )}
        {actions && (
          <div className={styles.HeroInTune__actions}>
            {actions}
          </div>
        )}
      </div>
    </section>
  )
}
